import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import BlogHeader from 'sections/blog/BlogHeader';
import BlogListTop from 'sections/blog/BlogListTop';
import ContactSection from 'sections/landing/ContactSection';
import Error404 from 'pages/maintenance/404';
import { SEO, buildBreadcrumb } from 'hooks/useSeo';
import { PageUrl } from 'const/page-url';
import { ARTICLE_META } from 'const/blog';
import { buildArticleListJsonLd } from 'const/blog/jsonLd';
import { categoryPath, findCategory } from 'const/blog/categories';

// ==============================|| BLOG CATEGORY PAGE ||============================== //
//
// カテゴリごとの記事一覧(/blog/category/<slug>)。
//
// 全記事の一覧(/blog)と同じ BlogListTop を使い、category を渡して絞り込む。
// カテゴリごとにページを分けているのは、検索エンジンから見て
// 「このカテゴリの記事が集まっている場所」を独立したURLとして示すため。
// クエリでの絞り込み(/blog?category=...)にすると同じページとしか見えず、
// カテゴリ単位で順位が付かない。
//
// 記事が1本も無いカテゴリは404を出す。中身の無いページを検索エンジンに
// 拾わせると、サイト全体の評価を下げるため(カテゴリの定義だけ先に増やしても、
// 記事を書くまでは表に出ない)。

const BlogCategory: React.FC = () => {
    const { categorySlug } = useParams<{ categorySlug: string }>();
    const category = findCategory(categorySlug);
    const articles = category ? ARTICLE_META.filter((article) => article.category === category.label) : [];

    // カテゴリを切り替えたとき、前のページのスクロール位置が残らないようにする
    // (記事ページと同じ理由。components/ScrollTop はアプリ起動時に一度動くだけ)
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [categorySlug]);

    if (!category || articles.length === 0) {
        return <Error404 />;
    }

    return (
        <React.Fragment>
            <SEO
                title={`${category.heading} | OHANA AI`}
                description={category.description}
                path={categoryPath(category.slug)}
                jsonLd={[
                    buildBreadcrumb([
                        { name: 'お役立ち記事', path: PageUrl.Blog.urlWithSlash },
                        { name: category.label, path: categoryPath(category.slug) }
                    ]),
                    buildArticleListJsonLd(category, articles, categoryPath(category.slug))
                ]}
            />
            <BlogHeader />
            <BlogListTop category={category} />
            <ContactSection to="/" />
        </React.Fragment>
    );
};

export default BlogCategory;
